import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@mui/styles';
import { Button, Card, CardHeader, CardContent, CardActions, Grid, Divider, TextField, colors } from '@mui/material';
import { SelectField } from 'components';
import DatePicker from 'components/DatePicker/DatePicker';
import validate from 'validate.js';

import { RanReportRFClusterDaily, RanReportRFClusterHourly, SetFormStateCluster } from '../../actions/RanReportRFActions';

const useStyles = makeStyles((theme) => ({
	root: {
		padding: theme.spacing(3),
	},
	container: {
		marginTop: theme.spacing(3),
	},
	tabs: {
		marginTop: theme.spacing(3),
	},
	content: {
		marginTop: theme.spacing(3),
	},
	generate: {
		marginLeft: 'auto',
		color: theme.palette.white,
		backgroundColor: colors.green[600],
		'&:hover': {
			backgroundColor: colors.green[900],
		},
	},
}));

const schema = {
	site: {
		presence: { allowEmpty: false, message: 'is required' }, length: { maximum: 1250 }
	},
	startDate: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	pre: {
		presence: { allowEmpty: false, message: 'is required' }, numericality: { greaterThanOrEqualTo: 1, lessThanOrEqualTo: 15 }
	},
	post: {
		presence: { allowEmpty: false, message: 'is required' }, numericality: { greaterThanOrEqualTo: 1, lessThanOrEqualTo: 15 }
	},
	aggregation: {
		presence: { allowEmpty: false, message: 'is required' },
	}
};

const RanReportRFClusterForm = () => {
	const classes = useStyles();
	const dispatch = useDispatch();
	const setFormStateCluster = formStateCluster => dispatch(SetFormStateCluster(formStateCluster));
	const formStateCluster = useSelector((state) => state.ranReportRFReducer.formStateCluster);

	const _hasError = (field) => (!!(formStateCluster.touched[field] && formStateCluster.errors[field]));

	const user = useSelector(state => state.session.user);

	const _generate = () => {
		if (formStateCluster.values.aggregation === 'Daily') {
			dispatch(RanReportRFClusterDaily(formStateCluster.values, user.id));
		} else if (formStateCluster.values.aggregation === 'Hourly') {
			dispatch(RanReportRFClusterHourly(formStateCluster.values, user.id));
		}
	};

	useEffect(() => {
		const errors = validate(formStateCluster.values, schema);

		setFormStateCluster({
			...formStateCluster,
			isValid: !errors,
			errors: errors || {},
		});
	}, [formStateCluster.values]);

	const _handleChange = (event) => {
		setFormStateCluster({
			...formStateCluster,
			values: {
				...formStateCluster.values,
				[event.target.name]:
					event.target.type === 'checkbox'
						? event.target.checked
						: event.target.value,
			},
			touched: {
				...formStateCluster.touched,
				[event.target.name]: true,
			},
		});
	};

	const _handleDateChange = (value, name) => {
		setFormStateCluster({
			...formStateCluster,
			values: {
				...formStateCluster.values,
				[name]: value,
			},
			touched: {
				...formStateCluster.touched,
				[name]: true,
			},
		});
	};

	return (
		<Card className={classes.content}>
			<CardHeader title="Cluster Report" />
			<Divider />
			<CardContent>
				<Grid container spacing={1}>
					<Grid item md={7} xs={12}>
						<TextField
							fullWidth
							label="Site(s) List"
							name="site"
							onChange={_handleChange}
							value={formStateCluster.values.site}
							variant="outlined"
							error={_hasError('site')}
							helperText={
								_hasError('site') ? formStateCluster.errors.site[0] : 'Example: 10500,10501,10502'
							}
						/>
					</Grid>
					<Grid item md={1} xs={12}>
						<TextField
							fullWidth
							label="PRE"
							name="pre"
							onChange={_handleChange}
							value={formStateCluster.values.pre}
							variant="outlined"
							error={_hasError('pre')}
							helperText={
								_hasError('pre') ? formStateCluster.errors.pre[0] : 'Example: 7'
							}
						/>
					</Grid>
					<Grid item md={1} xs={12}>
						<TextField
							fullWidth
							label="POST"
							name="post"
							onChange={_handleChange}
							value={formStateCluster.values.post}
							variant="outlined"
							error={_hasError('post')}
							helperText={
								_hasError('post') ? formStateCluster.errors.post[0] : 'Example: 7'
							}
						/>
					</Grid>
					<Grid item md={2} xs={12}>
						<DatePicker
							label="Implementation Date"
							name="startDate"
							value={formStateCluster.values.startDate || null}
							onChange={(value) => _handleDateChange(value, 'startDate')}
							error={_hasError('startDate')}
							helperText={_hasError('startDate') ? formStateCluster.errors.startDate[0] : null}
						/>
					</Grid>
					<Grid item md={1} xs={12}>
						<SelectField
							id="aggregation"
							name="aggregation"
							hintText="Aggregation"
							error={_hasError('aggregation')}
							helperText={
								_hasError('aggregation')
									? formStateCluster.errors.aggregation[0]
									: null
							}
							defaultValue=""
							menuItems={[{ value: 'Daily', text: 'Daily' }, { value: 'Hourly', text: 'Hourly' }]}
							value={formStateCluster.values.aggregation || ''}
							onChange={_handleChange}
						/>
					</Grid>
				</Grid>
			</CardContent>
			<Divider />
			<CardActions>
				<Button className={classes.generate} onClick={_generate} variant="contained" disabled={!formStateCluster.isValid}>Export</Button>
			</CardActions>
		</Card>
	);
};

export default RanReportRFClusterForm;
